import * as React from "react";
import {useEffect, useState} from "react";
import {t} from "i18next";
import {ChevronDownIcon, DownloadIcon} from "@radix-ui/react-icons";
import {Transition} from "@headlessui/react";
import {CustomDatePicker} from "../../components/CustomDatePicker";
import {toast} from "react-toastify";
import {useAxiosPrivate} from "../../hooks/useAxiosPrivate";
import {getLoggedInUsers, getRegisteredUsers} from "../../service/admin/userApi";
import {FlyoutSuperUserNavigationMenu} from "../../components/admin/FlyoutSuperUserNavigationMenu";

export const Users = (props) => {
    const {selectedMenuOption, setSelectedMenuOption} = props;

    // Hooks
    const axiosPrivate = useAxiosPrivate();

    // Filter Type Variables
    const [isRegisteredUsersTabSelected, setIsRegisteredUsersTabSelected] = useState(true);
    const [isLoggedInUsersFiltersTabSelected, setIsLoggedInUsersFiltersTabSelected] = useState(false);

    // Filter By Type Variables
    const filterByTypeOptions = [
        {
            label: t("SU_DASHBOARD.USERS.FILTER_BY.OPTIONS.DATE"),
            value: "date",
        },
        {
            label: t("SU_DASHBOARD.USERS.FILTER_BY.OPTIONS.DATE_RANGE"),
            value: "date-range",
        }
    ];

    const [isFilterByTypeDisabled, setIsFilterByTypeDisabled] = useState(false);

    const [isFilterByTypeDropdownOpen, setIsFilterByTypeDropdownOpen] = useState(false);
    const [isSelectingFilterByType, setIsSelectingFilterByType] = useState(false);

    const [selectedFilterByType, setSelectedFilterByType] = useState("date");

    // Date-Range
    const [selectedFromDate, setSelectedFromDate] = useState(new Date("2025-03-31"));
    const [selectedToDate, setSelectedToDate] = useState(new Date());

    // Date
    const [selectedCustomDate, setSelectedCustomDate] = useState(new Date());

    // Data
    const [registeredUsersData, setRegisteredUsersData] = useState([]);
    const [loggedInUsersData, setLoggedInUsersData] = useState([]);

    // User Activity Type Filter Functions
    const handleUserActivityFilterByTypeTabSelect = (tab) => {
        if (tab === "registered") {
            setIsRegisteredUsersTabSelected(true);
            setIsLoggedInUsersFiltersTabSelected(false);
        }

        if (tab === "logged-in") {
            setIsRegisteredUsersTabSelected(false);
            setIsLoggedInUsersFiltersTabSelected(true);
        }
    }

    // Filter By Functions
    const filterByTypeLabel = (filterByType) => {
        switch (filterByType) {
            case 'date-range':
                return t("SU_DASHBOARD.USERS.FILTER_BY.OPTIONS.DATE_RANGE");
            default:
                return t("SU_DASHBOARD.USERS.FILTER_BY.OPTIONS.DATE");
        }
    }

    const handleFilterByTypeSelect = (filterByType) => {
        setIsSelectingFilterByType(false);
        setIsFilterByTypeDropdownOpen(false);

        if (filterByType !== selectedFilterByType) {
            setSelectedFilterByType(filterByType);

            if (filterByType === "date-range") {
                setSelectedFromDate(new Date("2025-03-31"));
                setSelectedToDate(new Date());
            }
        }
    }

    const getRegisteredUsersData = async () => {
        try {
            const filters = {
                filterType: selectedFilterByType,
            }

            if (selectedFilterByType === "date-range") {
                filters.startDate = `${selectedFromDate.toISOString().split('T')[0]}`;
                filters.endDate = `${selectedToDate.toISOString().split('T')[0]}`;
            } else if (selectedFilterByType === "date") {
                filters.customDate = `${selectedCustomDate.toISOString().split('T')[0]}`;
            }

            const response = await getRegisteredUsers(axiosPrivate, filters);
            console.log("(SuperUser/Users.jsx) Get Registered Users Data Response: ", response);
            if (response.success) {
                setRegisteredUsersData(response.data);
            } else {
                setRegisteredUsersData([]);
            }
        } catch (error) {
            console.error("(Users.jsx) Error fetching registered users data: ", error);
            toast.error("Something went wrong while fetching registered users data!");
        }
    }

    const getLoggedInUsersData = async () => {
        try {
            const filters = {
                filterType: selectedFilterByType,
            }

            if (selectedFilterByType === "date-range") {
                filters.startDate = `${selectedFromDate.toISOString().split('T')[0]}`;
                filters.endDate = `${selectedToDate.toISOString().split('T')[0]}`;
            } else if (selectedFilterByType === "date") {
                filters.customDate = `${selectedCustomDate.toISOString().split('T')[0]}`;
            }

            const response = await getLoggedInUsers(axiosPrivate, filters);
            console.log("(SuperUser/Users.jsx) Get Logged-In Users Data Response: ", response);

            if (response.success) {
                setLoggedInUsersData(response.data);
            } else {
                setLoggedInUsersData([]);
            }
        } catch (error) {
            console.error("(Users.jsx) Error fetching Logged-In Users data: ", error);
            toast.error("Something went wrong while fetching Logged-In Users data!");
        }
    }

    useEffect(() => {
        if (selectedFilterByType === "date") {
            getLoggedInUsersData();
            getRegisteredUsersData();
        }
    }, [selectedCustomDate]);

    useEffect(() => {
        if (selectedFilterByType === "date-range") {
            if (selectedFromDate > selectedToDate) {
                toast.error("From date cannot be greater than To date!");
                setSelectedToDate(selectedFromDate);
            } else {
                getLoggedInUsersData();
                getRegisteredUsersData();
            }
        }
    }, [selectedFromDate, selectedToDate]);

    useEffect(() => {
        console.log("(Users.jsx) Users Component Mounted!");
        getLoggedInUsersData();
        getRegisteredUsersData();

        return () => {
            console.log("(Users.jsx) Users Component Unmounted!");
        };
    }, []);

    return (
        <div
            className="flex flex-col items-start justify-between h-full space-y-2
                portrait:p-[0.4rem] portrait:sm:p-[0.65rem] portrait:jk:p-[0.5rem]
                landscape:p-[0.4rem] landscape:jk:py-[0.5rem] landscape:jk:px-[0.75rem]"
        >
            {/* Header | Filters */}
            <div className={"flex flex-col items-start justify-between w-full landscape:gap-1 portrait:gap-2"}>
                {/* Header */}
                <div className={"flex items-center gap-2"}>
                    {/* Navigation Button for Mobile */}
                    <FlyoutSuperUserNavigationMenu
                        selectedMenuOption={selectedMenuOption}
                        setSelectedMenuOption={setSelectedMenuOption}
                    />

                    {/* Heading */}
                    <h1
                        className="font-custom-bold leading-tight
                        portrait:text-[1.5rem] portrait:md:text-[1.375rem]
                        portrait:jk:text-[1.5rem] portrait:lg:text-[2rem
                        landscape:text-[0.65rem] landscape:md:text-[0.875rem]
                        landscape:jk:text-[1rem] landscape:lg:text-[1.5rem]"
                    >
                        Users
                    </h1>
                </div>

                {/* Filters */}
                <div className={`w-full flex portrait:flex-col items-center justify-between portrait:gap-2`}>
                    {/* User Activity Type Filter Tab Bar */}
                    <div
                        className="portrait:w-full portrait:flex-1
                            landscape:w-80 relative whitespace-nowrap flex items-center
                            portrait:min-h-[2.75rem] portrait:h-[2.75rem] portrait:max-h-[2.75rem]
                            rounded-xl bg-[#E5E5E5]"
                    >
                        <div
                            className={`${isRegisteredUsersTabSelected
                                ? 'left-[0.25rem]'
                                : 'left-[calc(50%+0.25rem)]'} 
                                h-[calc(100%-0.5rem)]
                                w-[calc(50%-0.5rem)] flex-1 flex items-center justify-center
                                z-10 absolute bg-white rounded-lg border border-[#BEBEBE]`}
                            style={{
                                transition: "all 150ms cubic-bezier(0.215, 0.61, 0.355, 1)",
                                boxShadow: "0px 3px 8px rgba(255, 102, 0, 0.12), 0px 3px 1px rgba(255, 102, 0, 0.04)"
                            }}
                        />

                        <button
                            className="flex-1 flex items-center justify-center
                                z-20 bg-transparent h-full"
                            onClick={() => handleUserActivityFilterByTypeTabSelect("registered")}
                        >
                            <p
                                className="block font-custom-semi-bold leading-6 text-[#292D32]
                                    portrait:text-[1rem] portrait:md:text-[0.8rem]
                                    portrait:jk:text-[1rem] portrait:lg:text-[1.25rem]
                                    landscape:text-[0.65rem] landscape:md:text-[0.7rem]
                                    landscape:jk:text-[0.75rem] landscape:lg:text-[0.9rem]"
                            >
                                {t("SU_DASHBOARD.USERS.USER_ACTIVITY_TYPE.REGISTERED")}
                            </p>
                        </button>

                        <button
                            className="flex-1 flex items-center justify-center
                                z-20 bg-transparent h-full"
                            onClick={() => handleUserActivityFilterByTypeTabSelect("logged-in")}
                        >
                            <p
                                className="block font-custom-semi-bold leading-6 text-[#292D32]
                                    portrait:text-[1rem] portrait:md:text-[0.8rem]
                                    portrait:jk:text-[1rem] portrait:lg:text-[1.25rem]
                                    landscape:text-[0.65rem] landscape:md:text-[0.7rem]
                                    landscape:jk:text-[0.75rem] landscape:lg:text-[0.9rem]"
                            >
                                {t("SU_DASHBOARD.USERS.USER_ACTIVITY_TYPE.LOGGED_IN")}
                            </p>
                        </button>
                    </div>

                    {/* Filters */}
                    <div
                        className={`portrait:min-h-[2.5rem] portrait:h-[2.5rem] portrait:max-h-[2.5rem] 
                            h-full portrait:w-full landscape:w-fit flex landscape:items-center landscape:gap-2`}
                    >
                        {/* Filter By Type Options */}
                        <div
                            className="whitespace-nowrap w-32 landscape:flex-1 flex flex-col
                                rounded-md shadow-lg shadow-black/5"
                        >
                            <button
                                disabled={isFilterByTypeDisabled}
                                onBlur={() => {
                                    if (!isSelectingFilterByType) {
                                        setIsFilterByTypeDropdownOpen(false);
                                    }
                                }}
                                className={`custom-input-dropdown-button h-full
                                    ${isFilterByTypeDisabled ? 'bg-[#F2F2F2] cursor-default' : 'bg-white'}
                                    transition-all duration-150 ease-in-out`}
                                onClick={() => setIsFilterByTypeDropdownOpen(!isFilterByTypeDropdownOpen)}
                            >
                                <div className={""}>
                                    <p className={"!font-custom-semi-bold"}>
                                        {filterByTypeLabel(selectedFilterByType)}
                                    </p>
                                </div>
                                <ChevronDownIcon
                                    className={`${isFilterByTypeDropdownOpen ? 'rotate-180' : 'rotate-0'} `}
                                    aria-hidden="true"
                                    stroke={"#292D32"}
                                    strokeWidth={"0.1vh"}
                                />
                            </button>

                            {/* Filter By Type Dropdown */}
                            <div className={"relative"}>
                                <Transition show={isFilterByTypeDropdownOpen}>
                                    <div
                                        className={`bg-white absolute top-1 left-0 right-0 z-[100] max-h-[15vh]
                                            origin-top-right rounded-md shadow-2xl 
                                            border border-gray-700 overflow-hidden
                                            data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 
                                            data-[enter]:duration-100 data-[enter]:ease-out 
                                            data-[leave]:duration-75 data-[leave]:ease-in`}
                                    >
                                        {
                                            filterByTypeOptions.map((filterByType, index) => (
                                                <button
                                                    key={index}
                                                    className="custom-input-dropdown-list-button"
                                                    onMouseDown={() => setIsSelectingFilterByType(true)}
                                                    onClick={() => handleFilterByTypeSelect(filterByType.value)}
                                                >
                                                    <p className={""}>
                                                        {filterByType.label}
                                                    </p>
                                                </button>
                                            ))
                                        }
                                    </div>
                                </Transition>
                            </div>
                        </div>

                        {/* Filter By Type Fields */}
                        {
                            selectedFilterByType === "date-range" ? (
                                <div className={"flex gap-2"}>
                                    <CustomDatePicker
                                        selectedDate={selectedFromDate}
                                        setSelectedDate={setSelectedFromDate}
                                        label={t("SU_DASHBOARD.USERS.FILTER_BY.DATE_RANGE.FROM")}
                                    />
                                    <CustomDatePicker
                                        selectedDate={selectedToDate}
                                        setSelectedDate={setSelectedToDate}
                                        label={t("SU_DASHBOARD.USERS.FILTER_BY.DATE_RANGE.TO")}
                                    />
                                </div>
                            ) : (
                                <CustomDatePicker
                                    selectedDate={selectedCustomDate}
                                    setSelectedDate={setSelectedCustomDate}
                                />
                            )
                        }

                        {/* Export as Excel */}
                        <button
                            disabled={isFilterByTypeDisabled}
                            className={`custom-button-w-trailing-icon h-full landscape:flex-1 
                                portrait:!flex-[0] portrait:aspect-square portrait:items-center portrait:!justify-center
                                ${isFilterByTypeDisabled ? 'bg-[#F2F2F2] cursor-default' : 'bg-white'}
                                transition-all duration-150 ease-in-out`}
                            onClick={() => toast.info("Coming Soon!")}
                        >
                            <div className={"portrait:!hidden"}>
                                <p className={"whitespace-nowrap !font-custom-semi-bold"}>
                                    {`${t("SU_DASHBOARD.USERS.DOWNLOAD.LABEL")} ${t("SU_DASHBOARD.USERS.DOWNLOAD.OPTIONS.EXCEL")}`}
                                </p>
                            </div>
                            <DownloadIcon
                                aria-hidden="true"
                                stroke={"#292D32"}
                                strokeWidth={"0.1vh"}
                            />
                        </button>
                    </div>
                </div>
            </div>

            {/* Content - User List */}
            <div className="flex-1 h-full w-full max-w-full rounded-md border border-gray-300 relative overflow-hidden">
                <div className="flex-1 h-full w-full max-w-full max-h-full bg-white overflow-auto thin-scrollbar">
                    <table className="overflow-auto min-w-full table-auto">
                        <thead className={""}>
                        <tr className="bg-gray-100 rounded-t-lg">
                            <th className="p-2 sticky font-custom-regular text-left whitespace-nowrap">Photo</th>
                            <th className="p-2 sticky font-custom-regular text-left whitespace-nowrap">First Name</th>
                            <th className="p-2 sticky font-custom-regular text-left whitespace-nowrap">Middle Name</th>
                            <th className="p-2 sticky font-custom-regular text-left whitespace-nowrap">Phone Number</th>
                            <th className="p-2 sticky font-custom-regular text-left whitespace-nowrap">Email</th>
                            <th className="p-2 sticky font-custom-regular text-left whitespace-nowrap">Device</th>
                        </tr>
                        </thead>
                        <tbody>
                        {
                            (isRegisteredUsersTabSelected ? registeredUsersData : loggedInUsersData).length > 0 ? (
                                (isRegisteredUsersTabSelected ? registeredUsersData : loggedInUsersData).map((person, index) => (
                                    <tr
                                        key={index}
                                        className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}
                                    >
                                        <td className="p-2 whitespace-nowrap">
                                            {person.imageUrl ? (
                                                person.imageUrl.length <= 2 ? (
                                                    <div
                                                        className={`z-10 h-[5vh] w-auto aspect-square rounded-full overflow-hidden 
                                                                    flex items-center justify-center
                                                                    ${person.imageUrlBg}`}
                                                    >
                                                        <p className={"text-white font-custom-mono-bold"}>
                                                            {person.imageUrl.toUpperCase()}
                                                        </p>
                                                    </div>
                                                ) : (
                                                    <img
                                                        className="h-[5vh] w-auto aspect-square rounded-full"
                                                        src={person.imageUrl}
                                                        alt="User Profile Image"
                                                        referrerPolicy={"no-referrer"}
                                                    />
                                                )
                                            ) : (
                                                <img
                                                    className="h-[5vh] w-auto aspect-square rounded-full bg-[#D26A53]"
                                                    src={"/assets/default-pfp.png"}
                                                    alt="Default PFP"
                                                    referrerPolicy={"no-referrer"}
                                                />
                                            )}
                                        </td>
                                        <td className="p-2 whitespace-nowrap font-custom-regular">
                                            {person.firstName}
                                        </td>
                                        <td className="p-2 whitespace-nowrap font-custom-regular">
                                            {person.middleName}
                                        </td>
                                        <td className="p-2 whitespace-nowrap font-custom-regular">
                                            {person.phone?.number ? person.phone.dialingCode + ' ' + person.phone.number : ''}
                                        </td>
                                        <td className="p-2 whitespace-nowrap font-custom-regular">
                                            {person.email ? person.email : ''}
                                        </td>
                                        <td className="p-2 whitespace-nowrap font-custom-regular">
                                            {person.device ? person.device : ''}
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td
                                        colSpan="6"
                                        className="p-2 text-center text-gray-500 font-custom-medium-italic"
                                    >
                                        No data available
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Footer - Pagination */}
            {/*<div>*/}
            {/*    <h1 className="text-2xl font-custom-bold">*/}
            {/*        Users*/}
            {/*    </h1>*/}
            {/*</div>*/}
        </div>
    );
}
